import { useState } from "react"

import { Component, FieldErrorsObj, ParsedActionError } from "@/types"
import { cx } from "@/utils/cx"
import { executeCaptcha } from "@/actions/captcha/executeCaptcha"
import { demoSignUp } from "@/actions/_archive/auth/demoSignUp"
import {
  validateDemoSignUp,
  DemoSignUpKeys,
} from "@/actions/_archive/auth/demoSignUp/demoSignUp.interface"

import { Button } from "@/ui/atoms/controls/Button"
import { FieldGroup } from "@/ui/atoms/form/FieldGroup"
import { Form } from "@/ui/atoms/form/Form"
import { FormFields } from "@/ui/atoms/form/FormFields"
import { FormLinksWrapper } from "@/ui/atoms/form/FormLinksWrapper"
import { NotificationCard } from "@/ui/blocks/notifications/NotificationCard"
import { SplashCard } from "@/ui/atoms/layout/SplashCard"
import { Stack } from "@/ui/atoms/layout/Stack"
import { TextField } from "@/ui/atoms/form/TextField"
import { TextLink } from "@/ui/atoms/controls/TextLink"
import { Title } from "@/ui/atoms/type/Title"
import { BodyText } from "@/ui/atoms/type/BodyText"

import { errors } from "./DemoSignUpForm.errors"
import { useCourseStore } from "@/store/courseStoreProvider"

export const COMPONENT_NAME = "signUpForm"

export const presets = {}

export interface Props extends Component {
  onSuccess: (email: string) => void
}

export function DemoSignUpForm({ onSuccess, className, testId = COMPONENT_NAME }: Props) {
  const { CAPTCHA_SITE_KEY } = useCourseStore((s) => s.envVars)

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")

  const [formStatus, setFormStatus] = useState<"ready" | "submitting">("ready")
  const [formError, setFormError] = useState<null | ParsedActionError>(null)
  const [fieldErrors, setFieldErrors] = useState<null | FieldErrorsObj<DemoSignUpKeys>>(null)

  async function handleSubmit() {
    setFieldErrors(null)
    setFormStatus("submitting")

    const [recaptchaResponse, recaptchaError] = await executeCaptcha("signUp", CAPTCHA_SITE_KEY)
    if (recaptchaError) {
      setFormError(errors["CAPTCHA_FAILED"])
      setFormStatus("ready")
      return
    }

    const [validatedData, validationFieldErrors] = validateDemoSignUp({
      firstName,
      lastName,
      email,
      recaptchaResponse,
    })
    if (validationFieldErrors) {
      setFormError(errors["INVALID_INPUT"])
      setFieldErrors(validationFieldErrors)
      setFormStatus("ready")
      return
    }

    const [_, signUpErrorCode] = await demoSignUp(validatedData)

    if (signUpErrorCode) {
      setFormError(errors[signUpErrorCode])
      setFormStatus("ready")
      return
    }
    onSuccess(email)
  }

  return (
    <SplashCard className={cx(`w-full max-w-screen-form`, className)} testId={testId}>
      <Form name="Demo sign up" onSubmit={handleSubmit}>
        <header>
          <Stack className="gap-4">
            <Title size="lg" className="text-center">
              Course preview sign up
            </Title>
            <BodyText size="md" className="text-center">
              Experience a free sample of the course for 14 days
            </BodyText>
          </Stack>
        </header>
        <main className="flex flex-col gap-8">
          <FormFields>
            <FieldGroup>
              <TextField
                name="firstName"
                label="First name"
                error={fieldErrors?.firstName?.[0]}
                testId="firstNameField"
                autoComplete="given-name"
                onChange={(e) => setFirstName(e.target.value)}
              />
              <TextField
                name="lastName"
                error={fieldErrors?.lastName?.[0]}
                label="Last name"
                testId="lastNameField"
                autoComplete="family-name"
                onChange={(e) => setLastName(e.target.value)}
              />
            </FieldGroup>
            <FieldGroup>
              <TextField
                name="email"
                error={fieldErrors?.email?.[0]}
                type="email"
                label="Email"
                testId="emailField"
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </FieldGroup>
          </FormFields>

          {formError && (
            <Stack className="gap-3">
              <NotificationCard
                variant="failure"
                title={formError.title}
                text={formError.message}
                links={formError.links}
                className="border border-border-form border-l-0"
                testId="formErrorCard"
              />
            </Stack>
          )}
          <Button
            variant="primary"
            label="Sign up"
            width="full"
            type="submit"
            state={formStatus === "submitting" ? "loading" : "ready"}
            testId="submitButton"
          />
        </main>
        <footer className="">
          <FormLinksWrapper>
            <TextLink href="/sign-in" testId="signInLink" style="bold">
              Already have an account? Sign in
            </TextLink>
          </FormLinksWrapper>
        </footer>
      </Form>
    </SplashCard>
  )
}
